import * as THREE from 'three';
import type { BoardSnapshot, Coord, LevelSpec, MoveEvent, Piece } from '../logic/types';
import { VfxDirector } from './VfxDirector';
import { ArtFactory, LUMENWILD_CHAPTER_ART } from './ArtFactory';

export interface SceneCallbacks {
  swap: (from: Coord, to: Coord) => void;
  focus: (position: Coord) => void;
}

interface Motion {
  group: THREE.Group;
  from: THREE.Vector3;
  to: THREE.Vector3;
  age: number;
  duration: number;
  delay: number;
  remove?: boolean;
}

interface SceneTheme {
  background: string;
  deep: string;
  accent: string;
  glow: string;
}

export class GameScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
  readonly factory = new ArtFactory({ quality: 'balanced', patternCues: true });
  readonly vfx: VfxDirector;
  private readonly boardRoot = new THREE.Group();
  private readonly pieceRoot = new THREE.Group();
  private readonly cellRoot = new THREE.Group();
  private readonly environmentRoot = new THREE.Group();
  private readonly pieceViews = new Map<number, THREE.Group>();
  private readonly pieceSignatures = new Map<number, string>();
  private readonly hitViews: THREE.Object3D[] = [];
  private motions: Motion[] = [];
  private interactions = false;
  private level: LevelSpec | null = null;
  private snapshot: BoardSnapshot | null = null;
  private theme: SceneTheme = { background: '#111b35', deep: '#273857', accent: '#e96f64', glow: '#ffbe78' };
  private selected: Coord | null = null;
  private keyboardFocus: Coord | null = null;
  private pointerStart: { x: number; y: number; coord: Coord | null } | null = null;
  private selectionRing: THREE.Mesh | null = null;
  private elapsed = 0;
  private callbacks: SceneCallbacks;

  constructor(host: HTMLElement, callbacks: SceneCallbacks) {
    this.callbacks = callbacks;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.12;
    host.appendChild(this.renderer.domElement);
    this.vfx = new VfxDirector(this.scene, {
      cellSize: 0.86,
      boardNormal: new THREE.Vector3(0, 1, 0),
      coordinateToWorld: (position, target) => target.copy(this.toWorld(position)),
    });
    this.scene.add(this.boardRoot);
    this.boardRoot.add(this.cellRoot, this.pieceRoot);
    this.scene.add(this.environmentRoot);
    this.scene.fog = new THREE.Fog(this.theme.background, 12, 27);
    this.camera.position.set(0, 9.7, 10.8);
    this.camera.lookAt(0, 0, 0);
    this.createLights();
    this.bindInput();
    this.resize(host.clientWidth, host.clientHeight);
  }

  setPreferences(reducedMotion: boolean, reducedFlashes: boolean, highContrast = false): void {
    this.vfx.setReducedMotion(reducedMotion);
    if (this.snapshot && this.level) this.syncPieces(this.snapshot, [], false, highContrast);
  }

  setLevel(level: LevelSpec, snapshot: BoardSnapshot, highContrast = false): void {
    this.level = level;
    this.snapshot = snapshot;
    const palette = LUMENWILD_CHAPTER_ART[Math.max(0, Math.min(4, level.chapter - 1))];
    this.theme = { background: palette.background, deep: palette.primary, accent: palette.secondary, glow: palette.glow };
    this.scene.background = new THREE.Color(this.theme.background);
    this.scene.fog = new THREE.Fog(this.theme.background, 12, 27);
    this.motions = [];
    this.clearGroup(this.pieceRoot);
    this.pieceViews.clear();
    this.pieceSignatures.clear();
    this.clearGroup(this.cellRoot);
    this.hitViews.length = 0;
    this.clearGroup(this.environmentRoot);
    this.buildBoard(snapshot, highContrast);
    this.syncPieces(snapshot, [], false, highContrast);
    this.setCameraForBoard();
  }

  syncSnapshot(snapshot: BoardSnapshot, events: MoveEvent[] = [], highContrast = false): number {
    this.snapshot = snapshot;
    this.syncCellStates(snapshot, highContrast);
    const duration = this.syncPieces(snapshot, events, true, highContrast);
    this.vfx.playEvents(events);
    return duration;
  }

  update(delta: number): void {
    this.elapsed += delta;
    this.vfx.update(delta);
    for (let index = this.motions.length - 1; index >= 0; index -= 1) {
      const motion = this.motions[index];
      motion.age += delta;
      if (motion.age < motion.delay) continue;
      const t = Math.min(1, (motion.age - motion.delay) / motion.duration);
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      motion.group.position.lerpVectors(motion.from, motion.to, eased);
      if (!motion.remove) {
        const settle = Math.sin(Math.min(1, t) * Math.PI);
        motion.group.rotation.y = Math.sin(this.elapsed * 1.6 + Number(motion.group.userData.pieceId ?? 0)) * 0.025;
        motion.group.position.y += settle * 0.11;
      } else {
        motion.group.scale.setScalar(Math.max(0, 1 - t));
        motion.group.rotation.y += 0.08;
      }
      if (t >= 1) {
        if (motion.remove) {
          this.pieceRoot.remove(motion.group);
          this.disposeGroup(motion.group);
        } else {
          motion.group.position.copy(motion.to);
        }
        this.motions.splice(index, 1);
      }
    }
    this.environmentRoot.children.forEach((object, index) => {
      object.rotation.y += delta * (index % 2 ? -0.025 : 0.018);
      object.position.y += Math.sin(this.elapsed * 0.45 + index) * delta * 0.025;
      object.children.forEach((child) => {
        if (child.userData.twinkle !== undefined) child.scale.setScalar(0.85 + Math.sin(this.elapsed * 2.4 + child.userData.twinkle) * 0.15);
        if (child.userData.spin) child.rotation.z += delta * 0.8;
      });
    });
    if (this.selectionRing) {
      this.selectionRing.rotation.z += delta * 0.8;
      const pulse = 1 + Math.sin(this.elapsed * 7) * 0.06;
      this.selectionRing.scale.setScalar(pulse);
    }
    this.renderer.render(this.scene, this.camera);
  }

  resize(width: number, height: number): void {
    const safeHeight = Math.max(1, height);
    this.renderer.setSize(Math.max(1, width), safeHeight, false);
    this.camera.aspect = Math.max(0.55, width / safeHeight);
    this.camera.updateProjectionMatrix();
    this.setCameraForBoard();
  }

  setInputEnabled(enabled: boolean): void { this.interactions = enabled; }
  isAnimating(): boolean { return this.motions.length > 0; }

  showInvalid(from: Coord, to: Coord): void {
    this.vfx.invalidSwap(from, to, { color: '#ff7b7a' });
  }

  focus(position: Coord | null): void {
    this.keyboardFocus = position;
    this.selected = position;
    if (position) this.showSelection(position);
  }

  private buildBoard(snapshot: BoardSnapshot, highContrast: boolean): void {
    if (!this.level) return;
    this.boardRoot.position.y = 0;
    const size = this.cellSize();
    for (let r = 0; r < snapshot.height; r += 1) {
      for (let c = 0; c < snapshot.width; c += 1) {
        const cell = snapshot.cells[r][c];
        if (!cell.active) continue;
        const cellPosition = this.toWorld({ r, c });
        const view = new THREE.Group();
        view.position.copy(cellPosition);
        view.add(this.factory.createBoardCell({
          chapter: (this.level?.chapter ?? 1) - 1,
          active: cell.active,
          locked: cell.locked,
          hazard: cell.hazard,
          portal: cell.portal,
          conveyor: cell.conveyor,
          spawn: cell.spawn,
          exit: cell.exit,
          scale: size,
        }));
        const blocker = cell.blocker !== 'none'
          ? this.factory.createBlocker(cell.blocker, Math.max(0, Math.min(3, 3 - cell.layers)), { layers: cell.layers, scale: size })
          : null;
        if (blocker) { blocker.position.y = 0.02; view.add(blocker); }
        const hit = new THREE.Mesh(new THREE.PlaneGeometry(size * 0.95, size * 0.95), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }));
        hit.rotation.x = -Math.PI / 2;
        hit.position.y = 0.02;
        hit.userData.coord = { r, c };
        hit.userData.hit = true;
        view.add(hit);
        this.hitViews.push(hit);
        this.cellRoot.add(view);
      }
    }
    this.environmentRoot.position.set(0, -0.2, -3.6);
    const accents = this.factory.createChapterAccents((this.level?.chapter ?? 1) - 1, {
      width: snapshot.width,
      height: snapshot.height,
      cellSize: size,
      density: 8,
      seed: this.level?.seed,
      scale: 0.82,
    });
    this.environmentRoot.add(accents);
    const frame = this.factory.createBoardFrame({
      width: snapshot.width,
      height: snapshot.height,
      chapter: (this.level?.chapter ?? 1) - 1,
      cellSize: size,
    });
    frame.position.y = -0.05;
    frame.scale.set(1, 1, 1);
    this.boardRoot.add(frame);
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(snapshot.width * size + 7, snapshot.height * size + 7), new THREE.MeshStandardMaterial({ color: this.theme.deep, roughness: 0.88, metalness: 0.02 }));
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -0.5;
    plane.receiveShadow = true;
    this.boardRoot.add(plane);
    this.selectionRing = new THREE.Mesh(new THREE.TorusGeometry(size * 0.43, 0.025, 8, 26), new THREE.MeshBasicMaterial({ color: this.theme.glow, transparent: true, opacity: 0.82 }));
    this.selectionRing.rotation.x = Math.PI / 2;
    this.selectionRing.visible = false;
    this.boardRoot.add(this.selectionRing);
    this.setCameraForBoard();
    this.setInputEnabled(true);
    if (highContrast) this.renderer.toneMappingExposure = 1.28;
  }

  private syncCellStates(snapshot: BoardSnapshot, highContrast: boolean): void {
    // Rebuilding these light overlays is cheap (64 cells) and avoids any visual
    // divergence when a layered blocker unlocks in the same cascade.
    this.clearGroup(this.cellRoot);
    this.hitViews.length = 0;
    this.buildCellOverlays(snapshot, highContrast);
  }

  private buildCellOverlays(snapshot: BoardSnapshot, _highContrast: boolean): void {
    const size = this.cellSize();
    for (let r = 0; r < snapshot.height; r += 1) for (let c = 0; c < snapshot.width; c += 1) {
      const cell = snapshot.cells[r][c];
      if (!cell.active) continue;
      const view = new THREE.Group();
      view.position.copy(this.toWorld({ r, c }));
      view.add(this.factory.createBoardCell({
        chapter: (this.level?.chapter ?? 1) - 1,
        active: cell.active,
        locked: cell.locked,
        hazard: cell.hazard,
        portal: cell.portal,
        conveyor: cell.conveyor,
        spawn: cell.spawn,
        exit: cell.exit,
        scale: size,
      }));
      const blocker = cell.blocker !== 'none'
        ? this.factory.createBlocker(cell.blocker, Math.max(0, Math.min(3, 3 - cell.layers)), { layers: cell.layers, scale: size })
        : null;
      if (blocker) { blocker.position.y = 0.02; view.add(blocker); }
      const hit = new THREE.Mesh(new THREE.PlaneGeometry(size * 0.95, size * 0.95), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }));
      hit.rotation.x = -Math.PI / 2;
      hit.position.y = 0.02;
      hit.userData.coord = { r, c };
      hit.userData.hit = true;
      view.add(hit);
      this.hitViews.push(hit);
      this.cellRoot.add(view);
    }
  }

  private syncPieces(snapshot: BoardSnapshot, events: MoveEvent[], animate: boolean, highContrast: boolean): number {
    const next = new Map<number, { piece: Piece; position: Coord }>();
    for (let r = 0; r < snapshot.height; r += 1) for (let c = 0; c < snapshot.width; c += 1) {
      const piece = snapshot.pieces[r][c];
      if (piece) next.set(piece.id, { piece, position: { r, c } });
    }
    const duration = animate ? 0.58 : 0;
    for (const [id, group] of this.pieceViews) {
      if (next.has(id)) continue;
      const from = group.position.clone();
      this.motions.push({ group, from, to: from.clone().add(new THREE.Vector3(0, 0.15, 0)), age: 0, duration: 0.32, delay: 0, remove: true });
      this.pieceViews.delete(id);
      this.pieceSignatures.delete(id);
    }
    for (const [id, target] of next) {
      const signature = `${target.piece.type}:${target.piece.special}`;
      let group = this.pieceViews.get(id);
      if (!group || this.pieceSignatures.get(id) !== signature) {
        const oldPosition = group?.position.clone();
        if (group) {
          this.pieceRoot.remove(group);
          this.disposeGroup(group);
        }
        group = this.factory.createPiece(target.piece.type, target.piece.special, { scale: 0.84, muted: highContrast });
        this.pieceViews.set(id, group);
        this.pieceSignatures.set(id, signature);
        this.pieceRoot.add(group);
        group.position.copy(oldPosition ?? this.toWorld(target.position).add(new THREE.Vector3(0, animate ? 2.5 : 0, 0)));
      }
      const destination = this.toWorld(target.position).add(new THREE.Vector3(0, 0.2, 0));
      const from = group.position.clone();
      group.userData.coord = target.position;
      if (!animate) group.position.copy(destination);
      else this.motions.push({ group, from, to: destination, age: 0, duration: duration + (target.piece.age === 0 ? 0.12 : 0), delay: target.piece.age === 0 ? 0.06 : 0 });
    }
    if (!animate) return 0;
    if (events.some((event) => event.kind === 'combo')) return 0.92;
    if (events.some((event) => event.kind === 'cascade' && (event.cascade ?? 0) > 2)) return 0.86;
    return 0.68;
  }

  private showSelection(position: Coord): void {
    if (!this.selectionRing) return;
    this.selectionRing.visible = true;
    this.selectionRing.position.copy(this.toWorld(position)).add(new THREE.Vector3(0, 0.04, 0));
    this.vfx.clearSelection();
    this.vfx.selection(position, true, { color: this.theme.glow });
  }

  private clearSelection(): void {
    this.vfx.clearSelection();
    this.selected = null;
    if (this.selectionRing) this.selectionRing.visible = false;
  }

  private pointerCoord(event: PointerEvent): Coord | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const normalized = new THREE.Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(normalized, this.camera);
    const hits = raycaster.intersectObjects(this.hitViews, false);
    return hits[0]?.object.userData.coord ?? null;
  }

  private bindInput(): void {
    const canvas = this.renderer.domElement;
    canvas.addEventListener('pointerdown', (event) => {
      if (!this.interactions) return;
      canvas.setPointerCapture(event.pointerId);
      this.pointerStart = { x: event.clientX, y: event.clientY, coord: this.pointerCoord(event) };
    });
    canvas.addEventListener('pointerup', (event) => {
      if (!this.interactions || !this.pointerStart) return;
      const start = this.pointerStart;
      this.pointerStart = null;
      const end = this.pointerCoord(event);
      if (!start.coord) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.hypot(dx, dy) > 20) {
        const to = Math.abs(dx) > Math.abs(dy)
          ? { r: start.coord.r, c: start.coord.c + (dx > 0 ? 1 : -1) }
          : { r: start.coord.r + (dy > 0 ? 1 : -1), c: start.coord.c };
        this.trySwap(start.coord, to);
      } else if (end) {
        if (this.selected && this.isAdjacent(this.selected, end)) this.trySwap(this.selected, end);
        else {
          this.selected = end;
          this.keyboardFocus = end;
          this.showSelection(end);
          this.callbacks.focus(end);
        }
      }
    });
    canvas.addEventListener('pointercancel', () => { this.pointerStart = null; });
    window.addEventListener('keydown', (event) => {
      if (!this.interactions) return;
      const start = this.keyboardFocus ?? { r: Math.floor((this.level?.height ?? 8) / 2), c: Math.floor((this.level?.width ?? 8) / 2) };
      const direction: Record<string, Coord> = { ArrowUp: { r: -1, c: 0 }, ArrowDown: { r: 1, c: 0 }, ArrowLeft: { r: 0, c: -1 }, ArrowRight: { r: 0, c: 1 }, w: { r: -1, c: 0 }, s: { r: 1, c: 0 }, a: { r: 0, c: -1 }, d: { r: 0, c: 1 } };
      if (direction[event.key]) {
        event.preventDefault();
        const delta = direction[event.key];
        const next = { r: start.r + delta.r, c: start.c + delta.c };
        if (this.snapshot && next.r >= 0 && next.r < this.snapshot.height && next.c >= 0 && next.c < this.snapshot.width && this.snapshot.cells[next.r][next.c].active) {
          this.keyboardFocus = next;
          this.showSelection(next);
          this.callbacks.focus(next);
        }
      } else if ((event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        const focus = this.keyboardFocus ?? { r: Math.floor((this.level?.height ?? 8) / 2), c: Math.floor((this.level?.width ?? 8) / 2) };
        if (!this.selected) {
          this.selected = focus;
          this.showSelection(focus);
          this.callbacks.focus(focus);
        } else if (this.isAdjacent(this.selected, focus)) {
          this.trySwap(this.selected, focus);
        }
      }
    });
  }

  private trySwap(from: Coord, to: Coord): void {
    if (!this.snapshot || to.r < 0 || to.r >= this.snapshot.height || to.c < 0 || to.c >= this.snapshot.width) return;
    this.clearSelection();
    this.callbacks.swap(from, to);
  }

  private isAdjacent(a: Coord, b: Coord): boolean { return Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1; }

  private toWorld(position: Coord): THREE.Vector3 {
    const size = this.cellSize();
    const width = this.level?.width ?? 8;
    const height = this.level?.height ?? 8;
    return new THREE.Vector3((position.c - (width - 1) / 2) * size, 0.2, (position.r - (height - 1) / 2) * size);
  }

  private cellSize(): number {
    const width = this.level?.width ?? 8;
    const height = this.level?.height ?? 8;
    return Math.min(0.9, 6.0 / Math.max(width, height));
  }

  private setCameraForBoard(): void {
    const size = this.cellSize();
    const span = Math.max(this.level?.width ?? 8, this.level?.height ?? 8) * size;
    const mobile = this.camera.aspect < 0.85;
    const denseHud = (this.level?.objectives.length ?? 0) >= 3;
    const framing = denseHud ? 1.12 : 1;
    // A portrait viewport has a much narrower horizontal field of view at the
    // same perspective angle. Open the lens there so the whole constellation
    // remains readable instead of cropping its outer columns.
    this.camera.fov = mobile ? 64 : 34;
    // Keep the whole playable silhouette inside the safe area. The previous
    // close framing made the bottom row feel luxurious on desktop but clipped
    // it under the footer on shorter laptops and portrait tablets.
    this.camera.position.set(0, mobile ? span * 1.32 * framing : span * 1.4 * framing, mobile ? span * 1.58 * framing : span * 1.75 * framing);
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(0, denseHud ? (mobile ? 1.6 : 1.15) : (mobile ? 1.35 : 0.85), 0);
  }

  private createLights(): void {
    this.scene.add(new THREE.HemisphereLight('#c6dbff', '#172043', 2.25));
    const keyLight = new THREE.DirectionalLight('#fff3d1', 3.6);
    keyLight.position.set(-4, 9, 6);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    keyLight.shadow.camera.left = -7;
    keyLight.shadow.camera.right = 7;
    keyLight.shadow.camera.top = 7;
    keyLight.shadow.camera.bottom = -7;
    this.scene.add(keyLight);
    const rim = new THREE.PointLight('#78d9ff', 2.2, 14, 2);
    rim.position.set(4, 3.5, -3);
    this.scene.add(rim);
    const warm = new THREE.PointLight('#ffd18e', 1.7, 10, 2);
    warm.position.set(-4, 2, 4);
    this.scene.add(warm);
  }

  private clearGroup(group: THREE.Group): void {
    while (group.children.length) {
      const child = group.children.pop()!;
      this.disposeGroup(child);
    }
  }

  private disposeGroup(object: THREE.Object3D): void {
    // ArtFactory prototypes share both geometry and material caches. Removing
    // the instance is sufficient; the factory owns disposal at shutdown.
    object.clear();
  }
}

export default GameScene;
